/**
 * A single row in the notification inbox.
 *
 * ⚠️ Renders `title` / `body` / relative time and NOTHING keyed on `kind`.
 * The nine-value `kind` enum lives in `apps/bff/src/notification-kinds.ts`,
 * which is not a dependency of this app and is a two-sided contract with
 * `app-api-service` that still has one open item (see
 * `.claude/skills/notifications/SKILL.md`). Duplicating those strings here —
 * even for something as small as a per-kind icon — creates a second source of
 * truth that will drift the moment the set changes upstream. Every kind has a
 * title, a body and a timestamp, so that is what this row is built from.
 *
 * `body` is personal data about the customer's property (ADR-0006) — it is
 * rendered, never logged.
 */
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from '../i18n/format.ts';
import type { Notification } from '../api/notifications.ts';
import styles from './NotificationRow.module.css';

interface Props {
  notification: Notification;
  onOpen: (notification: Notification) => void;
}

export function NotificationRow({ notification, onOpen }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const isUnread = notification.readAt === null;

  return (
    <li className={styles['item']}>
      <button
        type="button"
        className={`${styles['row']} ${isUnread ? styles['unread'] : ''}`}
        onClick={() => onOpen(notification)}
      >
        <span className={styles['indicator']} aria-hidden="true" />

        <span className={styles['content']}>
          <span className={styles['top']}>
            <span className={styles['title']}>
              {isUnread && <span className={styles['sr-only']}>{t('notifications.unread')}. </span>}
              {notification.title}
            </span>
            <time className={styles['time']} dateTime={notification.createdAt}>
              {formatRelativeTime(notification.createdAt)}
            </time>
          </span>

          {notification.body !== null && notification.body !== '' && (
            <span className={styles['body']}>{notification.body}</span>
          )}
        </span>
      </button>
    </li>
  );
}
